import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import WaypointCategoryValue from '@alltrails/amplitude/enums/WaypointCategoryValue';
import WaypointEntryPoint from '@alltrails/amplitude/enums/WaypointEntryPoint';
import WaypointSourceKindValue from '@alltrails/amplitude/enums/WaypointSourceKindValue';
import WaypointSourceView from '@alltrails/amplitude/enums/WaypointSourceView';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import logWaypointDeleted from '@alltrails/amplitude/events/logWaypointDeleted';
import PlaceFilled from '@alltrails/denali/icons/PlaceFilled';
import { COLOR_MAP_WAYPOINT_USER } from '@alltrails/denali/tokens';
import {
  clearWaypointEditingState,
  removeWaypoint,
  selectCustomRouteWaypointById,
  setWaypointEditingState,
  updateWaypointDraft
} from '@alltrails/maps/redux/customRoute';
import type { CustomRouteWaypointId } from '@alltrails/types/CustomRoute';
import { getWaypointCategoryConfig } from '@alltrails/waypoints/utils/waypointCategories';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import BigButton from '../BigButton/BigButton';
import DeleteDialog from '../DeleteDialog';
import getDefaultWaypointName from './getDefaultWaypointName';
import styles from './Waypoint.module.scss';

type WaypointProps = {
  canEdit: boolean;
  waypointId: CustomRouteWaypointId;
  displayIndex: number;
  shouldHideBorder?: boolean;
  onWaypointClick: (id: number) => void;
  onWaypointEditClick?: (id: number) => void;
};

const Waypoint = ({ canEdit, waypointId, displayIndex, shouldHideBorder, onWaypointClick, onWaypointEditClick }: WaypointProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { selectedWaypointId, isEditing, activeWaypointId } = useAppSelector(state => ({
    selectedWaypointId: state.customRoute.selectedWaypointId,
    isEditing: state.customRoute.isEditing,
    activeWaypointId: state.customRoute.activeWaypointId
  }));
  const waypoint = useAppSelector(state => selectCustomRouteWaypointById(state, waypointId));
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const onClick = useCallback(() => {
    logCustomRouteUpdated({ custom_route_action_taken: 'waypoint clicked' });
    onWaypointClick(waypointId);
  }, [waypointId, onWaypointClick]);

  const onEditClick = useCallback(() => {
    onWaypointEditClick?.(waypointId);
  }, [onWaypointEditClick, waypointId]);

  const onDeleteWaypoint = useCallback(() => {
    logWaypointDeleted({
      category: waypoint?.waypointCategory?.uid as WaypointCategoryValue,
      entry_point: 'custom_routes' as WaypointEntryPoint,
      is_global: false,
      source_kind: 'route' as WaypointSourceKindValue,
      source_view: 'list_row' as WaypointSourceView,
      waypoint_id: waypoint?.serverId
    });
    dispatch(updateWaypointDraft(null));
    dispatch(removeWaypoint(waypointId));
    setIsDeleteDialogOpen(false);
  }, [dispatch, waypoint?.serverId, waypoint?.waypointCategory?.uid, waypointId]);

  if (!waypoint) {
    return null;
  }

  const { name, latitude, longitude } = waypoint;
  const categoryConfig = getWaypointCategoryConfig(intl, waypoint.waypointCategory?.uid);
  const iconComponent = categoryConfig ? categoryConfig.icon : PlaceFilled;

  const subtext = waypoint.description || categoryConfig?.name || `${latitude}, ${longitude}`;

  return (
    <>
      <BigButton
        disabled={!canEdit}
        icon={{ Component: iconComponent, color: '--color-text-primary-inverse' }}
        iconBackgroundColor={COLOR_MAP_WAYPOINT_USER}
        isActive={!selectedWaypointId && activeWaypointId === waypointId}
        onClick={onClick}
        onEditOptionClick={onWaypointEditClick ? onEditClick : undefined}
        onRemove={isEditing ? () => setIsDeleteDialogOpen(true) : undefined}
        onMouseEnter={() => dispatch(setWaypointEditingState({ activeId: waypointId }))}
        onMouseLeave={() => dispatch(clearWaypointEditingState())}
        selected={selectedWaypointId === waypointId}
        shouldTruncate={true}
        shouldHideBorder={shouldHideBorder}
        showOptionsMenu={isEditing}
        subtext={<div className={styles.subtext}>{subtext}</div>}
        text={name || getDefaultWaypointName(displayIndex, intl)}
      />
      {isDeleteDialogOpen && <DeleteDialog type="waypoint" onDeleteClick={onDeleteWaypoint} setIsOpen={setIsDeleteDialogOpen} />}
    </>
  );
};

export default Waypoint;
